import { useState, useEffect, useCallback, useMemo } from 'react'
import { legacyContextService } from '@/services/legacy/LegacyContextService'
import type { SourceOfTruth } from '@/types/api'

/** Domain labels mapped to their StructuredBusinessContext keys. */
const DOMAIN_KEYS = [
  { key: 'mission', label: 'Mission & Vision' },
  { key: 'targetMarket', label: 'Market Research' },
  { key: 'businessModel', label: 'Business Model' },
  { key: 'product', label: 'Product' },
  { key: 'goToMarket', label: 'Go-to-Market' },
  { key: 'competition', label: 'Competition' },
  { key: 'team', label: 'Team & Ops' },
  { key: 'fundraising', label: 'Fundraising' },
] as const

export type DomainStatus = 'complete' | 'partial' | 'missing'

export interface DomainInfo {
  key: string
  label: string
  status: DomainStatus
  /** Number of non-empty fields within this section. */
  filledFields: number
  /** Total possible fields in this section. */
  totalFields: number
}

/** Count non-empty fields in a section object. */
function countFields(section: Record<string, unknown> | undefined): {
  filled: number
  total: number
} {
  if (!section || typeof section !== 'object') return { filled: 0, total: 0 }
  const entries = Object.entries(section)
  const total = entries.length
  const filled = entries.filter(([, v]) => {
    if (v == null) return false
    if (typeof v === 'string') return v.trim().length > 0
    if (Array.isArray(v)) return v.length > 0
    return true
  }).length
  return { filled, total }
}

function deriveStatus(filled: number, total: number): DomainStatus {
  if (total === 0 || filled === 0) return 'missing'
  if (filled >= total) return 'complete'
  return 'partial'
}

export function useSourceOfTruth() {
  const [sot, setSot] = useState<SourceOfTruth | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await legacyContextService.legacyGetSourceOfTruth()
      setSot(data)
    } catch (err) {
      console.error('Failed to fetch source of truth:', err)
      setError('Failed to load context data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetch()
  }, [fetch])

  const domains: DomainInfo[] = useMemo(() => {
    if (!sot) return DOMAIN_KEYS.map((d) => ({ ...d, status: 'missing' as const, filledFields: 0, totalFields: 0 }))
    return DOMAIN_KEYS.map(({ key, label }) => {
      const section = sot[key as keyof SourceOfTruth] as Record<string, unknown> | undefined
      const { filled, total } = countFields(section)
      return { key, label, status: deriveStatus(filled, total), filledFields: filled, totalFields: total }
    })
  }, [sot])

  const healthPercent = useMemo(() => {
    const totalFields = domains.reduce((s, d) => s + d.totalFields, 0)
    if (totalFields === 0) return 0
    const filledFields = domains.reduce((s, d) => s + d.filledFields, 0)
    return Math.round((filledFields / totalFields) * 100)
  }, [domains])

  return { sot, domains, healthPercent, loading, error, refetch: fetch }
}
